// Streaming chunków wokół gracza: priorytet w kierunku ruchu, limit równoległych pobrań, LRU.
import { API_BASE } from '../config.js';
import { decodeChunk, type Chunk } from './chunkCodec.js';
import { Grid } from './coords.js';

const MAX_PARALLEL = 4;

export class ChunkStore {
  private chunks = new Map<string, Chunk | null>();   // null = poza światem
  private loading = new Set<string>();
  private queue: [number, number][] = [];
  onLoaded: (c: Chunk) => void = () => {};
  onEvicted: (cx: number, cy: number) => void = () => {};

  constructor(public grid: Grid, public radius = 2, public keep = 4) {}

  key(cx: number, cy: number): string { return `${cx},${cy}`; }
  get(cx: number, cy: number): Chunk | null | undefined { return this.chunks.get(this.key(cx, cy)); }

  /** Kolizja kafelka. Chunk niewczytany albo poza światem = zablokowany (tak samo jak na serwerze). */
  blocked = (tx: number, ty: number): boolean => {
    const n = this.grid.chunkTiles;
    const cx = Math.floor(tx / n), cy = Math.floor(ty / n);
    const c = this.chunks.get(this.key(cx, cy));
    if (!c) return true;
    return c.collision[(ty - cy * n) * n + (tx - cx * n)] === 1;
  };

  update(x: number, y: number, vx = 0, vy = 0): void {
    const [pcx, pcy] = this.grid.worldToChunk(x, y);
    const want: [number, number, number][] = [];
    for (let j = -this.radius; j <= this.radius; j++) {
      for (let i = -this.radius; i <= this.radius; i++) {
        const k = this.key(pcx + i, pcy + j);
        if (this.chunks.has(k) || this.loading.has(k)) continue;
        const ahead = i * Math.sign(vx + vy) + j * Math.sign(vy - vx);   // przybliżony kierunek w siatce
        want.push([pcx + i, pcy + j, Math.abs(i) + Math.abs(j) - ahead * 0.5]);
      }
    }
    want.sort((a, b) => a[2] - b[2]);
    this.queue = want.map(([a, b]) => [a, b]);
    this.pump();
    for (const k of [...this.chunks.keys()]) {
      const [cx, cy] = k.split(',').map(Number);
      if (Math.abs(cx - pcx) > this.keep || Math.abs(cy - pcy) > this.keep) {
        this.chunks.delete(k);
        this.onEvicted(cx, cy);
      }
    }
  }

  private pump(): void {
    while (this.loading.size < MAX_PARALLEL && this.queue.length) {
      const [cx, cy] = this.queue.shift()!;
      const k = this.key(cx, cy);
      this.loading.add(k);
      fetch(`${API_BASE}/api/v1/world/chunks/${cx}/${cy}.bin`)
        .then(async (res) => {
          if (res.status === 404) { this.chunks.set(k, null); return; }
          if (!res.ok) throw new Error(String(res.status));
          const chunk = await decodeChunk(await res.arrayBuffer());
          this.chunks.set(k, chunk);
          this.onLoaded(chunk);
        })
        .catch(() => { /* spróbujemy przy następnym update */ })
        .finally(() => { this.loading.delete(k); this.pump(); });
    }
  }

  get loadedCount(): number { return this.chunks.size; }
}
